// =============================================================================
// Inventory Lifecycle — faithful port of IMS Depleted_Inventory /
// Undeplete_Inventory + Update_Inventory_Year (active inventory year). Deplete
// or restore items, and set the active inventory year used for new stock.
// =============================================================================
import { useCallback, useEffect, useState } from 'react'
import { Archive, RotateCcw, CalendarClock, Save } from 'lucide-react'
import {
  searchProducts, listDepletedProducts, setProductDepleted, getSetting, setSetting,
} from '../services/data'
import { PageHeader, Card, Spinner, Button, SearchInput } from '../components/shared/ui'
import { currency, productCode } from '../lib/format'
import type { Product } from '../types'
import { toast } from 'react-toastify'

type Tab = 'active' | 'depleted'

export default function InventoryLifecycle() {
  const [tab, setTab] = useState<Tab>('depleted')
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [year, setYear] = useState('')
  const [savedYear, setSavedYear] = useState('')

  const load = useCallback(async (t: Tab, term: string) => {
    setLoading(true)
    try {
      const data = t === 'depleted' ? await listDepletedProducts(term) : await searchProducts(term, 200)
      // The Active tab should not list items already depleted.
      setRows(t === 'active' ? data.filter(p => !p.is_depleted) : data)
    } catch (e) { console.error(e); toast.error('Failed to load inventory') }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { getSetting('active_inventory_year').then(v => { setYear(v ?? ''); setSavedYear(v ?? '') }).catch(() => {}) }, [])
  useEffect(() => { load(tab, search) }, [load, tab]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveYear = async () => {
    if (!/^\d{4}$/.test(year.trim())) { toast.error('Enter a 4-digit year'); return }
    try { await setSetting('active_inventory_year', year.trim()); setSavedYear(year.trim()); toast.success(`Active inventory year set to ${year.trim()}`) }
    catch { toast.error('Failed to save active year') }
  }

  const deplete = async (p: Product, depleted: boolean) => {
    setRows(rs => rs.filter(r => r.id !== p.id))
    try {
      await setProductDepleted(p.id, depleted)
      toast.success(`${p.name} ${depleted ? 'depleted' : 'restored'}`)
    } catch { toast.error('Update failed'); load(tab, search) }
  }

  return (
    <div>
      <PageHeader title="Inventory Lifecycle" subtitle="Deplete / restore stock and set the active inventory year" />

      <Card className="p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <CalendarClock size={18} className="text-green-700" />
          <span className="text-sm text-gray-700">Active Inventory Year</span>
          <input value={year} onChange={e => setYear(e.target.value)} maxLength={4}
            className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm" placeholder="2026" />
          <Button variant="secondary" onClick={saveYear} disabled={year.trim() === savedYear}><Save size={15} /> Set Year</Button>
          {savedYear && <span className="text-xs text-gray-500">Current: <span className="font-semibold">{savedYear}</span></span>}
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-100 flex-wrap">
          <div className="flex rounded-lg overflow-hidden border border-gray-300">
            <button onClick={() => setTab('depleted')} className={`px-4 py-1.5 text-sm ${tab === 'depleted' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}>Depleted</button>
            <button onClick={() => setTab('active')} className={`px-4 py-1.5 text-sm ${tab === 'active' ? 'bg-green-600 text-white' : 'bg-white text-gray-600'}`}>Active</button>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-md">
            <div className="flex-1"><SearchInput value={search} onChange={setSearch} placeholder="Search name or code…" /></div>
            <Button variant="secondary" onClick={() => load(tab, search)}>Search</Button>
          </div>
        </div>

        {loading ? <div className="p-6"><Spinner label="Loading…" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-gray-600">
                  <th className="text-left px-3 py-2.5 font-semibold">Code</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Description</th>
                  <th className="text-left px-3 py-2.5 font-semibold">Type</th>
                  <th className="text-right px-3 py-2.5 font-semibold">On Hand</th>
                  <th className="text-right px-3 py-2.5 font-semibold">Retail</th>
                  <th className="px-3 py-2.5 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-gray-500">{tab === 'depleted' ? 'No depleted items.' : 'No active items found.'}</td></tr>
                ) : rows.map(p => (
                  <tr key={p.id} className="border-b border-gray-100">
                    <td className="px-3 py-1.5 font-medium text-gray-900">{productCode(p)}</td>
                    <td className="px-3 py-1.5 text-gray-700">{p.name}</td>
                    <td className="px-3 py-1.5 text-gray-500">{p.inventory_type || '—'}</td>
                    <td className="px-3 py-1.5 text-right text-gray-600">{p.qty_on_hand ?? 0}</td>
                    <td className="px-3 py-1.5 text-right">{currency(p.retail_price)}</td>
                    <td className="px-3 py-1.5 text-right">
                      {tab === 'depleted' ? (
                        <button onClick={() => deplete(p, false)} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"><RotateCcw size={14} /> Undeplete</button>
                      ) : (
                        <button onClick={() => deplete(p, true)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><Archive size={14} /> Deplete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
